import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { chartPaint } from '../tokens';

export interface StatTileProps {
  value: ReactNode;
  label: ReactNode;
  className?: string;
}

/** The small white stat cards on the profile sidebar — trips, response rate. */
export function StatTile({ value, label, className }: StatTileProps) {
  return (
    <div className={cn('rounded-card border border-line bg-surface p-[18px]', className)}>
      <div className="font-display text-display-xs font-extrabold tabular">{value}</div>
      <div className="text-meta text-muted">{label}</div>
    </div>
  );
}

export interface EcoFigureProps {
  /** Already formatted with thousands separators — "1,240,860". */
  value: string;
  unit?: string;
  caption?: ReactNode;
  /** Rendered above the figure in the accent colour. */
  eyebrow?: ReactNode;
  size?: 'md' | 'lg';
  className?: string;
}

/** The big CO₂ number inside a ngahere panel. */
export function EcoFigure({
  value,
  unit = 'kg CO₂ saved',
  caption,
  eyebrow,
  size = 'lg',
  className,
}: EcoFigureProps) {
  return (
    <div className={className}>
      {eyebrow ? (
        <p className="m-0 mb-3.5 text-caption font-semibold tracking-caps text-ngahere-fore uppercase">
          {eyebrow}
        </p>
      ) : null}
      <div className="flex items-baseline gap-2.5">
        <span
          className={cn(
            'font-display font-bold tracking-display tabular',
            size === 'lg' ? 'text-stat-2xl' : 'text-stat',
          )}
        >
          {value}
        </span>
        <span className="text-title-sm text-ngahere-fore">{unit}</span>
      </div>
      {caption ? <p className="mt-3.5 mb-0 text-base text-ngahere-body">{caption}</p> : null}
    </div>
  );
}

export interface MonthlyBar {
  /** Three-letter month — "Sep". */
  month: string;
  /** Kilograms of CO₂ saved that month. */
  value: number;
}

export interface EcoBarChartProps {
  data: readonly MonthlyBar[];
  className?: string;
}

/**
 * Monthly CO₂ savings. The tallest bar is 130px and takes the full ngahere;
 * every other bar is scaled against it in the muted green.
 */
export function EcoBarChart({ data, className }: EcoBarChartProps) {
  const peak = Math.max(...data.map((bar) => bar.value), 1);

  return (
    <div className={cn('flex h-[150px] items-end gap-2.5 pt-2.5', className)}>
      {data.map((bar) => (
        <div
          key={bar.month}
          className="flex h-full flex-1 flex-col items-center justify-end gap-[7px]"
        >
          <span className="text-micro text-subtle tabular">{bar.value} kg</span>
          <div
            className="w-full rounded-t-md transition-[height] duration-slow ease-standard"
            style={{
              height: `${Math.round((bar.value / peak) * 130)}px`,
              background: bar.value === peak ? chartPaint.barPeak : chartPaint.bar,
            }}
          />
          <span className="text-tiny font-semibold text-muted">{bar.month}</span>
        </div>
      ))}
    </div>
  );
}
